import { useRouter } from 'next/router'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Separator } from '@wartek-id/separator'
import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'
import { Spinner } from '@wartek-id/spinner'

import { Header } from 'app/KinerjaShared/components/Header'
import { usePelaksanaanPengembanganKompetensi } from 'app/KinerjaPenilikPegawaiPage/hooks/usePelaksanaanPengembanganKompetensi'
import ListKegiatan from './ListKegiatan'
import { usePenilikTrackEvent } from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikTrackEvent'
import usePenilikPeriodGuard from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikPeriodGuard'
import { PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'

const MAX_LENGTH_INPUT = 250

const MSG_ERR_REQUIRED = 'Wajib diisi.'
const MSG_ERR_MAX_LENGTH = `Maksimal ${MAX_LENGTH_INPUT} karakter.`
const MSG_ERR_SUBMIT = 'Gagal kirim Pengembangan Kompetensi. Coba lagi.'
const MSG_SUCCESS_SUBMIT = 'Berhasil kirim Pengembangan Kompetensi!'

const PelaksanaanPengembanganKompetensiPage = () => {
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = usePenilikTrackEvent()
  const [isPageLoading, setPageLoading] = useState(true)

  // Entry Point Guards
  const { isPeriodSuccess, pelaksanaanGuard } = usePenilikPeriodGuard()

  const { pelaksanaan, submitPelaksanaan, apiStatus, apiStatusSubmit } =
    usePelaksanaanPengembanganKompetensi()

  const [isOpenDialogKegiatan, setIsOpenDialogKegiatan] = useState(false)
  const [isShowForm, setIsShowForm] = useState(false)
  const { register, setValue, watch, formState, handleSubmit } = useForm({
    defaultValues: {
      pengembanganKompetensiPelaksanaanInspirasi: '',
      pengembanganKompetensiPelaksanaanPenerapanInspirasi: '',
    },
  })

  const {
    pengembanganKompetensiPelaksanaanInspirasi,
    pengembanganKompetensiPelaksanaanPenerapanInspirasi,
  } = watch()

  const submitForm = async () => {
    try {
      await submitPelaksanaan({
        pengembanganKompetensiPelaksanaanInspirasi,
        pengembanganKompetensiPelaksanaanPenerapanInspirasi,
      })

      trackEvent('pelaksanaan pegawai', 'submit pengembangan kompetensi')

      toast({
        message: MSG_SUCCESS_SUBMIT,
        type: 'success',
      })
      setTimeout(() => {
        router.push(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
      }, 1000)
    } catch (error) {
      toast({
        message: MSG_ERR_SUBMIT,
      })
    }
  }

  useEffect(() => {
    setValue(
      'pengembanganKompetensiPelaksanaanInspirasi',
      pelaksanaan?.pengembanganKompetensiPelaksanaanInspirasi ?? ''
    )
    setValue(
      'pengembanganKompetensiPelaksanaanPenerapanInspirasi',
      pelaksanaan?.pengembanganKompetensiPelaksanaanPenerapanInspirasi ?? ''
    )
    setIsShowForm(
      !pelaksanaan?.isEditable ||
        !!pelaksanaan?.pengembanganKompetensiPelaksanaanInspirasi ||
        !!pelaksanaan?.pengembanganKompetensiPelaksanaanPenerapanInspirasi
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pelaksanaan])

  useEffect(() => {
    if (isPeriodSuccess && !pelaksanaanGuard.isOpen) {
      router.replace(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
    } else if (isPeriodSuccess && apiStatus === 'success') {
      setPageLoading(false)
    }
  }, [apiStatus, isPeriodSuccess, pelaksanaanGuard.isOpen, router])

  if (isPageLoading) {
    return (
      <div className="flex justify-center mt-10">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <div className="mx-auto max-w-7xl mt-9">
        <Header
          title="Pengembangan Kompetensi"
          className="print:hidden"
          onBack={router.back}
        />
      </div>

      <div className="pt-10 pb-32 mx-auto max-w-7xl">
        <div className="flex gap-3">
          <div className="flex flex-col flex-1 gap-3 max-w-96">
            <div className="p-4 bg-white border rounded border-subdued">
              <Text variant="heading-sm">Indikator pilihan Anda</Text>

              <Separator className="my-2" />

              <Text color="subdued">
                {pelaksanaan?.pengembanganKompetensiIndikator.deskripsi}
              </Text>
            </div>

            <div
              className="p-4 bg-white border rounded border-subdued"
              data-testid="section-kegiatan"
            >
              <Text variant="heading-sm">
                Kegiatan Anda (
                {pelaksanaan?.pengembanganKompetensiKegiatanValues.length})
              </Text>

              <Separator className="my-4" />

              <ListKegiatan
                pengembanganKompetensiKegiatanValues={
                  pelaksanaan?.pengembanganKompetensiKegiatanValues ?? []
                }
                limit={1}
              />

              <Button
                size="sm"
                variant="ghost"
                className="mt-4"
                onClick={() => setIsOpenDialogKegiatan(true)}
              >
                Tampilkan semua kegiatan Anda
                <Icon fontSize="sm" color="informational" className="ml-1">
                  chevron_right
                </Icon>
              </Button>
            </div>

            <div className="p-4 border rounded border-subdued">
              <div className="flex gap-1">
                <Icon>info</Icon>
                <Text>
                  <strong>Tidak perlu kumpulkan Bukti Dukung</strong> untuk
                  Pengembangan Kompetensi.
                </Text>
              </div>
            </div>
          </div>

          <div className="self-start flex-1 bg-white border rounded border-subdued">
            <div className="p-6 rounded-t bg-surface-selected-default">
              <Text variant="heading-lg">Isi Refleksi</Text>
              <Text className="mt-1">
                Anda dapat ubah pengisian secara berkala selama Penetapan
                Predikat Kinerja belum dilakukan.
              </Text>
            </div>

            <div className="p-6">
              {isShowForm ? (
                <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
                  {!pelaksanaan?.isEditable && (
                    <div className="flex gap-2 mb-6">
                      <Icon color="informational">info</Icon>
                      <Text className="!text-blue-60">
                        Isi tidak dapat diubah lagi karena Penetapan Predikat
                        Kinerja sudah dilakukan.
                      </Text>
                    </div>
                  )}

                  <div className="flex flex-col gap-1 space-y-4">
                    <Text>
                      <strong>
                        Apa inspirasi menarik yang Anda peroleh dari kegiatan?
                      </strong>
                    </Text>

                    <div className="space-y-3">
                      <Text>Ketik maksimal 3 inspirasi:</Text>
                      <Textarea
                        placeholder="Ketik di sini"
                        {...register(
                          'pengembanganKompetensiPelaksanaanInspirasi',
                          {
                            required: {
                              value: true,
                              message: MSG_ERR_REQUIRED,
                            },
                            maxLength: {
                              value: MAX_LENGTH_INPUT,
                              message: MSG_ERR_MAX_LENGTH,
                            },
                          }
                        )}
                        isDisabled={!pelaksanaan?.isEditable}
                        isInvalid={
                          !!formState.errors
                            .pengembanganKompetensiPelaksanaanInspirasi?.message
                        }
                        rows={3}
                        data-testid="field-pengembanganKompetensiPelaksanaanInspirasi"
                      />
                      <div className="flex justify-between">
                        <Text
                          variant="helper"
                          color="critical"
                          className="text-right"
                        >
                          {
                            formState.errors
                              .pengembanganKompetensiPelaksanaanInspirasi
                              ?.message
                          }
                        </Text>

                        <Text
                          variant="helper"
                          color="subdued"
                          className="text-right"
                        >
                          {pengembanganKompetensiPelaksanaanInspirasi.length}/
                          {MAX_LENGTH_INPUT}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 space-y-4">
                    <Text>
                      <strong>
                        Bagaimana Anda menerapkan inspirasi tersebut untuk
                        kemajuan penguasaan kompetensi?
                      </strong>
                    </Text>

                    <div className="space-y-3">
                      <Text>
                        Ketik minimal 1 penerapan untuk kemajuan penguasaan
                        kompetensi Anda:
                      </Text>
                      <Textarea
                        placeholder="Ketik di sini"
                        {...register(
                          'pengembanganKompetensiPelaksanaanPenerapanInspirasi',
                          {
                            required: {
                              value: true,
                              message: MSG_ERR_REQUIRED,
                            },
                            maxLength: {
                              value: MAX_LENGTH_INPUT,
                              message: MSG_ERR_MAX_LENGTH,
                            },
                          }
                        )}
                        isDisabled={!pelaksanaan?.isEditable}
                        isInvalid={
                          !!formState.errors
                            .pengembanganKompetensiPelaksanaanPenerapanInspirasi
                            ?.message
                        }
                        rows={3}
                        data-testid="field-pengembanganKompetensiPelaksanaanPenerapanInspirasi"
                      />
                      <div className="flex justify-between">
                        <Text
                          variant="helper"
                          color="critical"
                          className="text-right"
                        >
                          {
                            formState.errors
                              .pengembanganKompetensiPelaksanaanPenerapanInspirasi
                              ?.message
                          }
                        </Text>

                        <Text
                          variant="helper"
                          color="subdued"
                          className="text-right"
                        >
                          {
                            pengembanganKompetensiPelaksanaanPenerapanInspirasi.length
                          }
                          /{MAX_LENGTH_INPUT}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={!pelaksanaan?.isEditable}
                      isLoading={apiStatusSubmit === 'loading'}
                      size="lg"
                      className="!px-12"
                    >
                      Kirim
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-6 py-4">
                  <Image
                    alt=""
                    src="/images/pengelolaan-kinerja/img-dokumen-refleksi.png"
                    width={287}
                    height={169}
                  />
                  <div className="max-w-72">
                    <Text variant="action">
                      Ceritakan inspirasi dan kemajuan penguasaan kompetensi
                      Anda.
                    </Text>
                    <Button
                      size="lg"
                      className="mt-3"
                      data-testid="btn-fill-form"
                      onClick={() => {
                        setIsShowForm(true)
                        trackEvent(
                          'pelaksanaan pegawai',
                          'start pengembangan kompetensi'
                        )
                      }}
                    >
                      Isi Refleksi
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Dialog
          size="md"
          isOpen={isOpenDialogKegiatan}
          onClose={() => setIsOpenDialogKegiatan(false)}
        >
          <DialogOverlay />
          <DialogContent>
            <DialogCloseButton />
            <DialogHeader>
              <Text variant="heading-md">
                Kegiatan (
                {pelaksanaan?.pengembanganKompetensiKegiatanValues.length})
              </Text>
            </DialogHeader>
            <div className="p-6" data-testid="dialog-kegiatan-content">
              <ListKegiatan
                pengembanganKompetensiKegiatanValues={
                  pelaksanaan?.pengembanganKompetensiKegiatanValues ?? []
                }
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  )
}

export default PelaksanaanPengembanganKompetensiPage
